import { parsePhoneNumberFromString, type E164Number } from 'libphonenumber-js'
import { useEffect, useRef, useState, type FC } from 'react'
import { components } from 'react-select'
import ReactPhoneInput, {
  type Country,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'react-phone-number-input'
import { FlagEmoji } from '@/components/Generic/FlagEmoji/FlagEmoji'
import { getFlagEmoji } from '@/methods/flagEmoji'
import { SelectInput, SelectOption } from '@/components/Generic/SelectInput/SelectInput'

export interface PhoneNumberValue {
  fullNumber: E164Number | undefined
  prefix: string
  nationalNumber: string
  isValid: boolean
}

interface PhoneInputProps {
  value: E164Number | undefined
  onChange: (value: PhoneNumberValue) => void
  disabled?: boolean
  defaultCountry?: Country
  language?: string
}

const languageToCountry: Record<string, Country> = {
  bg: 'BG',
  ca: 'ES',
  cs: 'CZ',
  da: 'DK',
  de: 'DE',
  el: 'GR',
  en: 'GB',
  es: 'ES',
  fi: 'FI',
  fr: 'FR',
  hu: 'HU',
  it: 'IT',
  nl: 'NL',
  no: 'NO',
  pl: 'PL',
  pt: 'PT',
  ro: 'RO',
  sk: 'SK',
  sl: 'SI',
  sv: 'SE',
}

interface CountrySelectOption extends SelectOption {
  icon?: string
}

interface CountrySelectProps {
  value: Country | undefined
  onChange: (country: Country) => void
  options: { value: Country | undefined; label: string }[]
  disabled?: boolean
}

const { Option, SingleValue } = components

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CountrySingleValue = (props: any) => {
  const callingCode = props.data.value ? `+${getCountryCallingCode(props.data.value)}` : ''

  return (
    <SingleValue {...props}>
      <div className="phone-country-select__item">
        <FlagEmoji
          className="phone-country-select__flag"
          emoji={getFlagEmoji(props.data.value)}
          label={props.data.label}
        />
        <span>{callingCode}</span>
      </div>
    </SingleValue>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CountryOption = (props: any) => {
  const callingCode = props.data.value ? `+${getCountryCallingCode(props.data.value)}` : ''

  return (
    <Option {...props}>
      <div className="phone-country-select__item">
        <FlagEmoji className="phone-country-select__flag" emoji={getFlagEmoji(props.data.value)} />
        <span>{props.data.label}</span>
        <span className="phone-country-select__code">{callingCode}</span>
      </div>
    </Option>
  )
}

function PhoneCountrySelect({ value, onChange, options, disabled }: CountrySelectProps) {
  const selectOptions: CountrySelectOption[] = options
    .filter((opt) => opt.value)
    .map((opt) => ({
      value: opt.value as string,
      label: opt.label,
    }))

  const selectedOption = selectOptions.find((opt) => opt.value === value) || null

  return (
    <SelectInput
      options={selectOptions}
      value={selectedOption}
      onChange={(e) => e && !Array.isArray(e) && onChange((e as SelectOption).value as Country)}
      isSearchable
      isDisabled={disabled}
      components={{
        SingleValue: CountrySingleValue,
        Option: CountryOption,
      }}
      className="phone-country-select"
      classNamePrefix="phone-country-select"
      menuPlacement="auto"
    />
  )
}

export const PhoneInput: FC<PhoneInputProps> = ({
  value,
  onChange,
  disabled = false,
  defaultCountry,
  language,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [country, setCountry] = useState<Country | undefined>(
    defaultCountry ?? (language ? languageToCountry[language] : undefined)
  )
  const resolvedDefaultCountry =
    defaultCountry ?? (language ? languageToCountry[language] : undefined)

  // Fix browser autofill: when autofill puts a full international number (e.g. "+447911123456")
  // into the national number input, react-phone-number-input stores the raw value internally
  // and doesn't re-format it (since the derived E164 is the same). We detect this by checking
  // if the tel input value starts with "+" (which should never happen for a national number)
  // and re-dispatch the change with just the national part.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleAutofill = (e: Event) => {
      const input = e.target as HTMLInputElement
      if (input.type !== 'tel' || !input.value.startsWith('+')) return

      const parsed = parsePhoneNumberFromString(input.value)
      if (!parsed?.nationalNumber) return

      // Let the library process the full number first (for country detection),
      // then fix the displayed value to show only the national number.
      setTimeout(() => {
        if (!input.value.startsWith('+')) return

        const nativeSetter = Object.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          'value'
        )?.set
        if (nativeSetter) {
          nativeSetter.call(input, parsed.nationalNumber)
          input.dispatchEvent(new Event('input', { bubbles: true }))
        }
      }, 0)
    }

    container.addEventListener('input', handleAutofill)
    return () => container.removeEventListener('input', handleAutofill)
  }, [])

  const handleFocus = () => {
    setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
  }

  const handleChange = (newValue: E164Number | undefined) => {
    const parsed = newValue ? parsePhoneNumberFromString(newValue) : undefined
    const detectedCountry = parsed?.country ?? country
    const prefix = detectedCountry ? `+${getCountryCallingCode(detectedCountry)}` : ''
    const nationalNumber = parsed?.nationalNumber ?? ''

    onChange({
      fullNumber: newValue,
      prefix,
      nationalNumber,
      isValid: newValue ? isValidPhoneNumber(newValue) : false,
    })
  }

  return (
    <div ref={containerRef} onFocus={handleFocus} className="phone-input-wrapper">
      <ReactPhoneInput
        disabled={disabled}
        value={value}
        onChange={handleChange}
        defaultCountry={resolvedDefaultCountry}
        country={country}
        onCountryChange={setCountry}
        countrySelectComponent={PhoneCountrySelect}
      />
    </div>
  )
}
